import {flatten} from 'lodash';
import {
  Plugins,
  Component,
  Expressions,
  matchExpression,
} from '@packages/systems/wfdl';
import {getLinkedFieldsPathMapForElement} from './LinkedFieldsPathUtils';

import {
  OVERRIDEABLE_DATA_PATHS,
  getOverridableDataPathsArray,
  type OverridableDataPath,
  type OverridableDataPathKey,
} from './OverridableDataPaths';

import {constant} from '@packages/utilities/fp/utils';
import {qualify} from '@packages/systems/wfdl-Qualified';
import type {NativeId} from '@packages/systems/core/utils/NativeIdUtils';
import type {Expression, ExpressionElement} from '@packages/systems/wfdl-ast';

export const OverrideEditingTarget: OverrideEditingTargetEnum = {
  None: 'None',
  Typographic: 'Typographic',
  RichText: 'RichText',
  ImageOrVideo: 'ImageOrVideo',
};

type OverrideEditingTargetEnum = {
  None: 'None';
  Typographic: 'Typographic';
  RichText: 'RichText';
  ImageOrVideo: 'ImageOrVideo';
};

type OverrideEditingTargetType = keyof OverrideEditingTargetEnum;

export type TargetBoundElement = {
  type: OverrideEditingTargetType;
  element: ExpressionElement;
};

export const isAnyFieldBound = (dataPaths: OverridableDataPath[]) =>
  Boolean(dataPaths.length);

export const getBoundFields = (
  element: ExpressionElement
): OverridableDataPath[] => {
  const elementData = Expressions.getElementData(element);

  return getOverridableDataPathsArray().reduce<Array<any>>((res, currData) => {
    // Check if the element data has an expression at the provided path
    const elementPathValue = Expressions.getIn(elementData, currData.path);
    if (!elementPathValue) return res;

    // Check if the expression contains an override field key
    const overrideFieldKey = getFieldId(elementPathValue);
    if (!overrideFieldKey) return res;

    return [...res, currData];
  }, []);
};

export const getBoundToTheOutermostFields = (
  outerSymInstance: ExpressionElement,
  selectedNativeIdPath: NativeId[],
  element: ExpressionElement,
  focusedSymbolNativeIdPath: NativeId[],
  components: Component.ComponentMap,
  plugins: Plugins.PluginsType
): OverridableDataPath[] => {
  // Get the linked fields between the selected node in instance and the outermost instance
  const linkedFieldsPathMap = getLinkedFieldsPathMapForElement(
    outerSymInstance,
    selectedNativeIdPath,
    focusedSymbolNativeIdPath,
    components,
    plugins
  );

  // These are the linked fields ids from the selected node in instance element to the outermost instance
  const linkedFieldsIds =
    linkedFieldsPathMap && flatten(Object.values(linkedFieldsPathMap));

  const boundFields = getBoundFields(element);

  const elementData = Expressions.getElementData(element);

  return boundFields.filter((pathData) => {
    const overridablePath = OVERRIDEABLE_DATA_PATHS[pathData.key];
    const exprAtPath = overridablePath
      ? Expressions.getIn(elementData, overridablePath.path)
      : null;
    const overrideFieldId = exprAtPath ? getFieldId(exprAtPath) : null;

    return linkedFieldsIds.includes(overrideFieldId);
  });
};

export const getOutermostFieldIdFromPath = (
  elementId: string,
  componentId: string,
  pathKey: OverridableDataPathKey,
  components: Component.ComponentMap,
  linkedFieldsPathMap: {
    [fieldId: string]: string[];
  }
): string => {
  const updatedComponent = components.get(
    qualify(Plugins.name('__SitePlugin'), Component.name(componentId))
  );
  if (!updatedComponent) {
    return '';
  }
  const updatedComponentRender = Component.getRender(updatedComponent);

  const updatedElement = Expressions.getElementById(
    updatedComponentRender,
    elementId
  );

  const exprAtKey = updatedElement
    ? Expressions.getIn(
        Expressions.getElementData(updatedElement),
        // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
        OVERRIDEABLE_DATA_PATHS[pathKey].path
      )
    : null;
  const fieldId = exprAtKey ? getFieldId(exprAtKey) : '';
  let linkedField = null;
  Object.keys(linkedFieldsPathMap).forEach((key) => {
    // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
    if (linkedFieldsPathMap[key].includes(fieldId)) {
      linkedField = key;
      return;
    }
  });
  return linkedField || fieldId;
};

const getFieldId: (expr: Expression) => string = matchExpression({
  Select: ({prop}) => prop,
  Call: ({arg}) => getFieldId(arg),
  _: constant(''),
});
